/**
 * WeeklyUpdateReviewModal — the governed review gate for the Weekly
 * Customer Update (Ask #2). The engine (`crm/weeklyUpdate.ts`) assembles
 * and renders the digest; THIS component is where the human approves it.
 *
 * Governance contract:
 *   - The operator sees the resolved recipient, an editable subject +
 *     body, and the four honest digest sections before anything sends.
 *   - No resolvable email → the send is disabled with the honest reason
 *     ("No contact email on file"), never a guessed address.
 *   - "Approve & send" queues the real email via the outbox→Brevo
 *     pipeline (`sendTransactionalEmail`) and ONLY THEN appends the
 *     hash-chained Decision Ledger entry (`logActivity`) — the ledger
 *     records sends that actually queued, nothing speculative.
 *   - The send is labeled NOT reversible, because it isn't: an email
 *     leaves the building.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Mail, Send, X } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { useFocusTrap } from '../lib/useFocusTrap';
import { useToast } from './ToastProvider';
import { sendTransactionalEmail } from '../lib/email';
import { logActivity } from '../lib/db/activity';
import {
  escapeHtml,
  renderWeeklyUpdateEmail,
  type WeeklyUpdateDigest,
  type WeeklyUpdateRecipient,
} from '../lib/crm/weeklyUpdate';
import type { Account } from '../lib/db/types';

interface Props {
  account: Account;
  digest: WeeklyUpdateDigest;
  /** Resolved via resolveWeeklyUpdateRecipient — null means "can't send". */
  recipient: WeeklyUpdateRecipient | null;
  onClose: () => void;
}

/** The four digest sections, in send order — mirrors the engine's render. */
const SECTIONS: { key: keyof Pick<WeeklyUpdateDigest, 'delivered' | 'inProgress' | 'next' | 'blockers'>; heading: string }[] = [
  { key: 'delivered', heading: 'Delivered' },
  { key: 'inProgress', heading: 'In progress' },
  { key: 'next', heading: 'Coming next' },
  { key: 'blockers', heading: 'Needs your input' },
];

export function WeeklyUpdateReviewModal({ account, digest, recipient, onClose }: Props) {
  const { push } = useToast();
  const rendered = useMemo(() => renderWeeklyUpdateEmail(digest), [digest]);
  const [subject, setSubject] = useState(rendered.subject);
  const [body, setBody] = useState(rendered.textContent);
  const [sending, setSending] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, sending]);

  const canSend = recipient !== null && !sending && subject.trim() !== '' && body.trim() !== '';

  async function onApproveAndSend() {
    if (!recipient) return;
    setSending(true);
    try {
      // If the operator edited the body, the pretty rendered HTML no
      // longer matches — send the edited text (escaped, pre-wrap) so what
      // was reviewed is exactly what goes out. Unedited keeps the styled HTML.
      const htmlContent =
        body === rendered.textContent
          ? rendered.htmlContent
          : `<div style="font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1a1a1a;background:#ffffff;white-space:pre-wrap;">${escapeHtml(body)}</div>`;

      await sendTransactionalEmail({
        to: recipient.email,
        subject: subject.trim(),
        textContent: body,
        htmlContent,
      });

      // Seal the send in the Decision Ledger — ONLY after the queue
      // succeeded. Best-effort by the ledger's own contract: a logging
      // failure must never re-report a queued send as failed.
      try {
        await logActivity({
          event: 'executed',
          source: 'chat',
          kind: 'weekly_update',
          summary: `Weekly update sent to ${account.name} (${recipient.email})`,
          subjectId: account.id,
        });
      } catch {
        push({
          kind: 'warning',
          text: 'Sent, but ledger entry failed',
          detail: 'The email queued, but the Decision Ledger append did not complete.',
        });
      }

      push({
        kind: 'success',
        text: 'Weekly update sent',
        detail: `Queued to ${recipient.email} — sealed in the Decision Ledger (Admin → Audit).`,
      });
      onClose();
    } catch (err) {
      push({
        kind: 'error',
        text: 'Send failed',
        detail: err instanceof Error ? err.message : 'Could not queue the email — nothing was sent.',
      });
    } finally {
      setSending(false);
    }
  }

  return createPortal(
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !sending) onClose();
      }}
      data-menu-portal="true"
    >
      <div
        ref={containerRef}
        className="account-modal-wrap"
        role="dialog"
        aria-modal="true"
        aria-labelledby="weekly-update-title"
        tabIndex={-1}
        style={{ width: 'min(640px, 100%)' }}
      >
        <GlassPanel variant="hero" className="account-modal" animate>
          <header
            className="account-modal__head"
            style={{ alignItems: 'center', borderBottom: 'none', paddingBottom: 4 }}
          >
            <Mail size={18} style={{ color: C.magentaSoft, marginRight: 6 }} />
            <div style={{ flex: 1 }}>
              <span className="t-eyebrow">Weekly customer update</span>
              <h2 id="weekly-update-title" className="t-section-title" style={{ margin: '4px 0 0' }}>
                Review before it sends — {account.name}
              </h2>
            </div>
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              aria-label="Close"
              disabled={sending}
            >
              <X size={13} />
            </button>
          </header>

          <div style={{ display: 'grid', gap: 14, padding: '12px 0 0' }}>
            {/* Recipient — resolved, never guessed. */}
            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                To
              </div>
              {recipient ? (
                <div className="t-card-title">
                  {recipient.name} <span className="t-meta">&lt;{recipient.email}&gt;</span>
                </div>
              ) : (
                <div
                  className="t-meta"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.orange }}
                >
                  <AlertTriangle size={13} /> No contact email on file — add a POC email to this
                  account (or a matching customer contact) to enable sending.
                </div>
              )}
            </div>

            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                Subject
              </div>
              <input
                className="glass-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={sending}
                maxLength={200}
              />
            </div>

            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                Body (plain text — edit before approving)
              </div>
              <textarea
                className="glass-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={sending}
                rows={12}
                style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.45 }}
              />
            </div>

            {/* The four digest sections, at a glance. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: 8,
              }}
            >
              {SECTIONS.map(({ key, heading }) => {
                const items = digest[key];
                return (
                  <div
                    key={key}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                      {heading} · {items.length}
                    </div>
                    {items.length === 0 ? (
                      <span className="t-meta">Nothing this week.</span>
                    ) : (
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
                        {items.map((item, i) => (
                          <li
                            key={`${item.title}-${i}`}
                            className="t-meta"
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={item.detail ? `${item.title} (${item.detail})` : item.title}
                          >
                            {item.title}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Honest irreversibility label. */}
            <div
              className="t-meta"
              style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.orange }}
            >
              <AlertTriangle size={13} aria-hidden="true" />
              <span>
                Not reversible — approving queues a real email to the customer. The send is
                stamped into the Decision Ledger (Admin → Audit).
              </span>
            </div>
          </div>

          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              paddingTop: 14,
              marginTop: 4,
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <button type="button" className="glass-btn" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={() => void onApproveAndSend()}
              disabled={!canSend}
              title={recipient ? 'Queue the email and seal it in the Decision Ledger' : 'No contact email on file'}
            >
              <Send size={13} /> {sending ? 'Sending…' : 'Approve & send'}
            </button>
          </footer>
        </GlassPanel>
      </div>
    </div>,
    document.body,
  );
}
